import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Form, InputNumber, Modal, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../../utils/date';
import { ok, runAction } from './utils';

const { Text } = Typography;
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; } };
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FREQ = { '6h': 'Every 6 hours', '12h': 'Every 12 hours', daily: 'Daily', weekly: 'Weekly' };

/** Scheduled snapshots: a button with the current schedule + its settings dialog (SnapshotScheduleService). */
const SnapshotSchedule = ({ id, api, disabled, active, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const freq = Form.useWatch('frequency', form);
    const enabled = Form.useWatch('enabled', form);

    const load = useCallback(async () => {
        if (!api.getSnapshotSchedule) return;
        const res = await api.getSnapshotSchedule(id);
        if (ok(res)) setData(res.data);
    }, [api, id]);

    useEffect(() => { if (active) load(); }, [active, load]);

    if (!data?.available) return null;
    const s = data.schedule;
    const on = !!s?.enabled;
    const supported = (data.disks || []).filter((d) => d.supported);
    // qcow2 file disks are snapshotted together (one whole-VM snapshot): checking one checks them all
    const fileIds = supported.filter((d) => d.whole_vm).map((d) => d.id);
    const onValuesChange = (changed) => {
        if (!changed.disk_ids || fileIds.length < 2) return;
        const prev = form.getFieldValue('_prev_disks') || [];
        const had = prev.some((i) => fileIds.includes(i));
        const has = changed.disk_ids.some((i) => fileIds.includes(i));
        const lostOne = had && fileIds.some((i) => prev.includes(i) && !changed.disk_ids.includes(i));
        let next = changed.disk_ids.filter((i) => !fileIds.includes(i));
        if (has && !lostOne) next = [...next, ...fileIds];
        form.setFieldsValue({ disk_ids: next, _prev_disks: next });
    };

    const openModal = () => {
        const sys = supported.find((d) => d.type === 'system') || supported[0];
        form.setFieldsValue(s ? {
            enabled: s.enabled, frequency: s.frequency, hour: s.hour, weekday: s.weekday, keep: s.keep, disk_ids: s.disk_ids,
        } : { enabled: true, frequency: 'daily', hour: 3, weekday: 0, keep: 1, disk_ids: sys ? [sys.id] : [] });
        const ids = form.getFieldValue('disk_ids') || [];
        const withFiles = ids.some((i) => fileIds.includes(i)) ? [...new Set([...ids, ...fileIds])] : ids;
        form.setFieldsValue({ disk_ids: withFiles, _prev_disks: withFiles });
        setOpen(true);
    };

    const save = async (v) => {
        setSaving(true);
        const res = await runAction(() => api.saveSnapshotSchedule(id, {
            enabled: v.enabled ? 1 : 0, frequency: v.frequency, hour: v.hour, weekday: v.weekday ?? 0, keep: v.keep,
            disk_ids: v.disk_ids || [], timezone: s?.timezone && s.enabled && !v.enabled ? s.timezone : browserTz(),
        }), t('Saved'), t('Save failed'));
        setSaving(false);
        if (res) { setData(res.data); setOpen(false); onChanged?.(); }
    };

    const hh = (h) => `${String(h).padStart(2, '0')}:00`;
    const summary = on ? `${t(FREQ[s.frequency])}${s.frequency === 'weekly' ? ` ${t(WEEKDAYS[s.weekday])}` : ''} ${hh(s.hour)}` : null;

    return (
        <>
            <Tooltip title={on ? `${t('Next')}: ${formatDateTime(s.next_run_at)}${s.last_status === 'error' && s.last_error ? ` · ${s.last_error}` : ''}` : t('Scheduled snapshots')}>
                <Button size="small" icon={<ClockCircleOutlined />} onClick={openModal} disabled={disabled || supported.length === 0}>
                    {on ? <Space size={4}>{summary}{s.last_status === 'error' && <Tag color="warning" style={{ marginInlineEnd: 0 }}>!</Tag>}</Space> : t('Scheduled')}
                </Button>
            </Tooltip>
            <Modal title={t('Scheduled snapshots')} open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} confirmLoading={saving} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={save} onValuesChange={onValuesChange}>
                    <Form.Item name="_prev_disks" hidden><Select mode="multiple" /></Form.Item>
                    <Form.Item name="enabled" valuePropName="checked" label={t('Enabled')}>
                        <Switch />
                    </Form.Item>
                    {enabled && (
                        <>
                            <Space wrap size={12} align="start">
                                <Form.Item name="frequency" label={t('Frequency')}>
                                    <Select style={{ width: 150 }} options={Object.entries(FREQ).map(([k, v]) => ({ value: k, label: t(v) }))} />
                                </Form.Item>
                                {freq === 'weekly' && (
                                    <Form.Item name="weekday" label={t('Day')}>
                                        <Select style={{ width: 130 }} options={WEEKDAYS.map((d, i) => ({ value: i, label: t(d) }))} />
                                    </Form.Item>
                                )}
                                <Form.Item name="hour" label={t('Time')} extra={browserTz()}>
                                    <Select style={{ width: 100 }} options={Array.from({ length: 24 }, (_, h) => ({ value: h, label: hh(h) }))} />
                                </Form.Item>
                                <Form.Item name="keep" label={t('Keep')} tooltip={t('Oldest automatic snapshot is replaced; manual snapshots are kept')}>
                                    <InputNumber min={1} max={data.quota} style={{ width: 90 }} />
                                </Form.Item>
                            </Space>
                            <Form.Item name="disk_ids" label={t('Disks')} rules={[{ required: true, type: 'array', min: 1 }]}>
                                <Checkbox.Group options={supported.map((d) => ({ value: d.id, label: `${d.dev} · ${d.size} GB` }))} />
                            </Form.Item>
                            {fileIds.length > 1 && <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -16, marginBottom: 8 }}>{t('Local disks are snapshotted together')}</Text>}
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Counts toward the snapshot quota ({{n}})', { n: data.quota })}</Text>
                            {s?.last_status === 'error' && s.last_error && <Alert style={{ marginTop: 12 }} type="warning" showIcon message={s.last_error} />}
                        </>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default SnapshotSchedule;
